import React, { useEffect, useMemo, useRef, useState } from 'react';

function LeafletRouteMap({ origin, destination, showDefaultMap, height, style }) {
  const mapRef = useRef(null);
  const routeLayerRef = useRef(null);
  const [L, setL] = useState(null);

  const containerId = useMemo(() => `leaflet-map-${Math.random().toString(36).slice(2)}` , []);
  const containerStyle = useMemo(() => ({
    width: '100%',
    height: height ? `${height}px` : '100%',
    borderRadius: '12px',
    marginTop: 16,
    ...style,
  }), [height, style]);

  // Dynamically import Leaflet in browser
  useEffect(() => {
    let cancelled = false;
    // Inject Leaflet CSS via CDN so bundler doesn't need local file
    const cssId = 'leaflet-css-cdn';
    if (typeof document !== 'undefined' && !document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    (async () => {
      try {
        const mod = await import('leaflet');
        if (cancelled) return;
        const _L = mod.default || mod;
        // Fix default icon paths (Leaflet expects assets)
        const iconRetinaUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png';
        const iconUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
        const shadowUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';
        delete _L.Icon.Default.prototype._getIconUrl;
        _L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });
        setL(_L);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Initialize map once
  useEffect(() => {
    if (!L || mapRef.current) return;
    const map = L.map(containerId, { zoomControl: false });
    map.setView([53.7267, -127.6476], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [L, containerId]);

  // Draw simple route (geocode both ends and connect by polyline)
  useEffect(() => {
    if (!L || !mapRef.current) return;
    const map = mapRef.current;
    const isString = v => typeof v === 'string' && v.trim().length > 0;

    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }

    if (isString(origin) && isString(destination)) {
      const geocode = async (q) => {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data = await res.json();
        return data && data[0] ? [parseFloat(data[0].lat), parseFloat(data[0].lon)] : null;
      };

      (async () => {
        try {
          const [a, b] = await Promise.all([geocode(origin), geocode(destination)]);
          if (a && b) {
            // Use OSRM to get an actual road route
            let routeCoords = null;
            try {
              const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${a[1]},${a[0]};${b[1]},${b[0]}?overview=full&geometries=geojson`;
              const osrmRes = await fetch(osrmUrl);
              const osrmData = await osrmRes.json();
              if (osrmData.routes && osrmData.routes[0]) {
                routeCoords = osrmData.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
              }
            } catch (e) {
              // fallback to straight line if OSRM fails
            }

            const markerA = L.marker(a);
            const markerB = L.marker(b);
            const line = routeCoords
              ? L.polyline(routeCoords, { color: '#1976d2', weight: 4, opacity: 0.8 })
              : L.polyline([a, b], { color: '#1976d2', weight: 4, opacity: 0.8, dashArray: '8,8' });

            const group = L.featureGroup([markerA, markerB, line]).addTo(map);
            map.fitBounds(group.getBounds().pad(0.3));
            routeLayerRef.current = group;
          }
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [L, origin, destination]);

  return (
    <div id={containerId} style={containerStyle} />
  );
}

export default LeafletRouteMap;
